import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  FileText,
  Play,
  Plus,
  Printer,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';

import { InvoiceForm } from './invoices/InvoiceForm.tsx';
import type { Invoice, InvoiceStatus } from './invoices/types.ts';

import logoSimple from '@/assets/logo.png';
import { ApiError, apiFetch } from '@/lib/api';
import { formatBRL } from '@/lib/utils';


interface ListResponse {
  rows: Array<Invoice & { client?: { id: string; razaoSocial: string; cpfCnpj: string } }>;
  total: number;
}

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  DRAFT: 'Rascunho',
  PENDING: 'Em emissão',
  ISSUED: 'Emitida',
  REJECTED: 'Rejeitada',
  CANCELLED: 'Cancelada',
};

const STATUS_STYLE: Record<InvoiceStatus, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  PENDING: 'bg-amber-500/10 text-amber-600',
  ISSUED: 'bg-emerald-500/10 text-emerald-600',
  REJECTED: 'bg-destructive/10 text-destructive',
  CANCELLED: 'bg-muted text-muted-foreground line-through',
};

const STATUS_ICON: Record<InvoiceStatus, typeof FileText> = {
  DRAFT: FileText,
  PENDING: Clock,
  ISSUED: CheckCircle2,
  REJECTED: XCircle,
  CANCELLED: AlertCircle,
};

function fmtDate(d: string): string {
  return new Date(d).toLocaleDateString('pt-BR');
}

export function InvoicesPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Invoice | 'new' | null>(null);
  const [viewing, setViewing] = useState<Invoice | null>(null);
  const [rejectionView, setRejectionView] = useState<Invoice | null>(null);
  const [nfseView, setNfseView] = useState<Invoice | null>(null);

  const queryKey = useMemo(() => ['invoices', { statusFilter, search }], [statusFilter, search]);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '200' });
      if (statusFilter !== 'ALL') params.set('status', statusFilter);
      if (search) params.set('search', search);
      return apiFetch<ListResponse>(`/invoices?${params}`);
    },
    refetchInterval: (q) => {
      const d = q.state.data as ListResponse | undefined;
      // Auto-refresh enquanto houver notas PENDING
      return d?.rows.some((r) => r.status === 'PENDING') ? 3000 : false;
    },
  });

  const emit = useMutation({
    mutationFn: (id: string) => apiFetch<{ id: string }>(`/invoices/${id}/emit`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/invoices/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Notas Fiscais</h1>
          <p className="text-sm text-muted-foreground">
            Emissão de NFSe pelo padrão SEFIN Nacional. Notas em "Em emissão" atualizam
            automaticamente.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Nova nota
          </button>
        </div>
      </header>

      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="search"
          placeholder="Buscar por número, protocolo ou descrição…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[280px] rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | 'ALL')}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="ALL">Todos os status</option>
          {(Object.keys(STATUS_LABEL) as InvoiceStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      <section className="rounded-lg border border-border bg-card">
        {isLoading && <p className="p-4 text-sm text-muted-foreground">Carregando…</p>}
        {isError && <p className="p-4 text-sm text-destructive">Erro ao carregar notas.</p>}
        {data && data.rows.length === 0 && (
          <div className="flex flex-col items-center gap-3 p-12 text-center">
            <FileText className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Nenhuma nota cadastrada.</p>
          </div>
        )}
        {data && data.rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Nº NFSe</th>
                <th className="px-4 py-2 font-medium">Cliente</th>
                <th className="px-4 py-2 font-medium">Competência</th>
                <th className="px-4 py-2 font-medium">Valor</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((inv) => {
                const StatusIcon = STATUS_ICON[inv.status];
                const editable = inv.status === 'DRAFT' || inv.status === 'REJECTED';
                return (
                  <tr
                    key={inv.id}
                    className="border-b border-border last:border-0 hover:bg-accent/30"
                  >
                    <td className="px-4 py-2 font-mono text-xs">{inv.numeroNfse ?? '—'}</td>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        onClick={() => setViewing(inv)}
                        className="font-medium hover:underline text-left"
                      >
                        {inv.client?.razaoSocial ?? '—'}
                      </button>
                      <div className="text-xs text-muted-foreground truncate max-w-xs">
                        {inv.servicoDescricao}
                      </div>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {inv.competencia.slice(0, 7)}
                    </td>
                    <td className="px-4 py-2">{formatBRL(Number(inv.valorServico))}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[inv.status]}`}
                      >
                        <StatusIcon className="h-3 w-3" />
                        {STATUS_LABEL[inv.status]}
                      </span>
                    </td>
                    <td className="px-2 py-2 flex justify-end gap-1">
                      {inv.status === 'ISSUED' && (
                        <button
                          type="button"
                          data-dica="Visualizar nota emitida"
                          onClick={() => setNfseView(inv)}
                          className="rounded-md p-2 text-emerald-600 hover:bg-emerald-500/10"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      )}
                      {inv.status === 'REJECTED' && (
                        <button
                          type="button"
                          data-dica="Ver motivo da rejeicao"
                          onClick={() => setRejectionView(inv)}
                          className="rounded-md p-2 text-destructive hover:bg-destructive/10"
                        >
                          <AlertCircle className="h-4 w-4" />
                        </button>
                      )}
                      {editable && (
                        <button
                          type="button"
                          data-dica="Emitir"
                          onClick={() => emit.mutate(inv.id)}
                          disabled={emit.isPending}
                          className="rounded-md p-2 text-primary hover:bg-primary/10 disabled:opacity-50"
                        >
                          <Play className="h-4 w-4" />
                        </button>
                      )}
                      {editable && (
                        <button
                          type="button"
                          data-dica="Editar"
                          onClick={() => setEditing(inv)}
                          className="rounded-md p-2 text-muted-foreground hover:bg-accent"
                        >
                          <FileText className="h-4 w-4" />
                        </button>
                      )}
                      {inv.status !== 'ISSUED' && (
                        <button
                          type="button"
                          data-dica="Excluir"
                          onClick={() => {
                            if (confirm('Excluir nota?')) remove.mutate(inv.id);
                          }}
                          className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {data && <p className="text-xs text-muted-foreground">{data.total} nota(s)</p>}

      {editing && (
        <InvoiceForm
          invoice={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['invoices'] });
            setEditing(null);
          }}
        />
      )}

      {viewing && (
        <InvoiceDetails
          invoice={viewing}
          onClose={() => setViewing(null)}
          onEmit={() => emit.mutate(viewing.id)}
        />
      )}

      {rejectionView && (
        <RejectionDetailsDialog
          invoice={rejectionView}
          onClose={() => setRejectionView(null)}
          onRetry={() => {
            emit.mutate(rejectionView.id);
            setRejectionView(null);
          }}
        />
      )}

      {nfseView && (
        <IssuedNfseDialog invoice={nfseView} onClose={() => setNfseView(null)} />
      )}
    </div>
  );
}

interface SefinErro {
  Codigo?: string;
  Descricao?: string;
  Complemento?: string;
}

function RejectionDetailsDialog({
  invoice,
  onClose,
  onRetry,
}: {
  invoice: Invoice & { client?: { id: string; razaoSocial: string; cpfCnpj: string } };
  onClose: () => void;
  onRetry: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['invoices', invoice.id, 'detail'],
    queryFn: () =>
      apiFetch<Invoice & { xmlEnvio?: string; xmlRetorno?: string }>(
        `/invoices/${invoice.id}`,
      ),
  });

  const errorMessage = data?.errorMessage ?? invoice.errorMessage ?? '';
  const xmlRetorno = data?.xmlRetorno ?? '';
  const xmlEnvio = data?.xmlEnvio ?? '';

  // SEFIN as vezes devolve JSON com array `erros`; outras vezes XML.
  // Tenta parsear JSON pra exibir bonito; senao mostra cru.
  let parsedErrors: SefinErro[] | null = null;
  if (xmlRetorno) {
    try {
      const trimmed = xmlRetorno.trim();
      if (trimmed.startsWith('{')) {
        const j = JSON.parse(trimmed) as { erros?: SefinErro[] };
        if (Array.isArray(j.erros)) parsedErrors = j.erros;
      }
    } catch {
      /* ignore */
    }
  }

  function copyToClipboard(text: string) {
    void navigator.clipboard.writeText(text);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-medium text-destructive">
              <AlertCircle className="h-5 w-5" />
              Rejeicao da SEFIN
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Cliente:{' '}
              <strong>{invoice.client?.razaoSocial ?? '—'}</strong> · Competencia{' '}
              {invoice.competencia.slice(0, 7)}
            </p>
          </div>
          <button
            type="button"
            onClick={onRetry}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Play className="h-4 w-4" /> Tentar emitir de novo
          </button>
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}

        {/* Erros parseados (SEFIN devolve JSON com array erros) */}
        {parsedErrors && parsedErrors.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Erros reportados pela SEFIN
            </h3>
            {parsedErrors.map((e, i) => (
              <div
                key={i}
                className="rounded-md border border-destructive/30 bg-destructive/5 p-3"
              >
                <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                  {e.Codigo && (
                    <span className="rounded bg-destructive/10 px-2 py-0.5 font-mono text-xs">
                      {e.Codigo}
                    </span>
                  )}
                  {e.Descricao}
                </div>
                {e.Complemento && (
                  <p className="mt-1 text-xs text-destructive/90">{e.Complemento}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Mensagem de erro generica (quando nao parseou) */}
        {!parsedErrors && errorMessage && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-destructive">
              Mensagem de erro
            </div>
            <pre className="mt-1 whitespace-pre-wrap text-xs text-destructive">
              {errorMessage}
            </pre>
          </div>
        )}

        {/* XML retorno cru (sempre disponivel pra debug) */}
        {xmlRetorno && (
          <details className="mt-4 rounded-md border border-border bg-muted/20 p-3">
            <summary className="flex cursor-pointer items-center justify-between text-xs font-medium">
              <span>Resposta bruta da SEFIN ({xmlRetorno.length} chars)</span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  copyToClipboard(xmlRetorno);
                }}
                className="rounded border border-input bg-background px-2 py-0.5 text-[10px] hover:bg-accent"
              >
                Copiar
              </button>
            </summary>
            <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap break-all text-[11px] font-mono">
              {xmlRetorno}
            </pre>
          </details>
        )}

        {xmlEnvio && (
          <details className="mt-2 rounded-md border border-border bg-muted/20 p-3">
            <summary className="flex cursor-pointer items-center justify-between text-xs font-medium">
              <span>XML enviado ({xmlEnvio.length} chars)</span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  copyToClipboard(xmlEnvio);
                }}
                className="rounded border border-input bg-background px-2 py-0.5 text-[10px] hover:bg-accent"
              >
                Copiar
              </button>
            </summary>
            <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap break-all text-[11px] font-mono">
              {xmlEnvio}
            </pre>
          </details>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function InvoiceDetails({
  invoice,
  onClose,
  onEmit,
}: {
  invoice: Invoice;
  onClose: () => void;
  onEmit: () => void;
}) {
  const qc = useQueryClient();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [cancelErr, setCancelErr] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', invoice.id, 'detail'],
    queryFn: () => apiFetch<Invoice & { xmlEnvio?: string; xmlRetorno?: string }>(`/invoices/${invoice.id}`),
    refetchInterval: (q) => {
      // Atualiza enquanto estiver em emissao/cancelamento (sem status intermediario,
      // checamos se sumiu a errorMessage entre tentativas de cancel)
      const d = q.state.data as Invoice | undefined;
      return d?.status === 'PENDING' ? 2000 : false;
    },
  });

  const cancel = useMutation({
    mutationFn: () =>
      apiFetch<{ id: string; status: InvoiceStatus }>(`/invoices/${invoice.id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ motivo: motivo.trim() }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['invoices', invoice.id, 'detail'] });
      setShowCancelDialog(false);
      setMotivo('');
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setCancelErr(err.message);
      } else {
        setCancelErr(err instanceof Error ? err.message : 'Erro ao cancelar.');
      }
    },
  });

  const StatusIcon = STATUS_ICON[(data?.status ?? invoice.status) as InvoiceStatus];
  const status = data?.status ?? invoice.status;
  const editable = status === 'DRAFT' || status === 'REJECTED';
  const cancellable = status === 'ISSUED' && !!(data?.numeroNfse ?? invoice.numeroNfse);

  function submitCancel(ev: FormEvent) {
    ev.preventDefault();
    setCancelErr(null);
    const trimmed = motivo.trim();
    if (trimmed.length < 15 || trimmed.length > 255) {
      setCancelErr('Motivo deve ter entre 15 e 255 caracteres.');
      return;
    }
    cancel.mutate();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium">
              Nota {data?.numeroNfse ?? '(sem número)'}
            </h2>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs mt-1 ${STATUS_STYLE[status]}`}
            >
              <StatusIcon className="h-3 w-3" /> {STATUS_LABEL[status]}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {editable && (
              <button
                type="button"
                onClick={() => {
                  onEmit();
                  onClose();
                }}
                className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                <Play className="h-4 w-4" /> Emitir
              </button>
            )}
            {cancellable && (
              <button
                type="button"
                onClick={() => {
                  setMotivo('');
                  setCancelErr(null);
                  setShowCancelDialog(true);
                }}
                className="flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
              >
                <Ban className="h-4 w-4" /> Cancelar nota
              </button>
            )}
          </div>
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}

        {data && (
          <dl className="space-y-3 text-sm">
            <Row label="Cliente" value={data.servicoDescricao} />
            <Row label="Competência" value={data.competencia.slice(0, 7)} />
            <Row label="Valor do serviço" value={formatBRL(Number(data.valorServico))} />
            <Row label="Alíquota ISS" value={`${Number(data.aliquotaIss).toFixed(2)}%`} />
            <Row label="Valor ISS" value={formatBRL(Number(data.valorIss))} />
            <Row label="Valor líquido" value={formatBRL(Number(data.valorLiquido))} />
            {data.protocolo && <Row label="Protocolo" value={data.protocolo} mono />}
            {data.codigoVerificacao && (
              <Row label="Código de verificação" value={data.codigoVerificacao} mono />
            )}
            {data.errorMessage && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
                <div className="text-xs font-medium text-destructive">Erro na emissão</div>
                <pre className="mt-1 whitespace-pre-wrap text-xs text-destructive">
                  {data.errorMessage}
                </pre>
              </div>
            )}

            {data.xmlEnvio && (
              <details className="rounded-md border border-border bg-muted/20 p-3">
                <summary className="cursor-pointer text-xs font-medium">XML enviado</summary>
                <pre className="mt-2 overflow-x-auto text-[11px] font-mono">{data.xmlEnvio}</pre>
              </details>
            )}
            {data.xmlRetorno && (
              <details className="rounded-md border border-border bg-muted/20 p-3">
                <summary className="cursor-pointer text-xs font-medium">XML retorno</summary>
                <pre className="mt-2 overflow-x-auto text-[11px] font-mono">{data.xmlRetorno}</pre>
              </details>
            )}
          </dl>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Fechar
          </button>
        </div>
      </div>

      {showCancelDialog && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={() => !cancel.isPending && setShowCancelDialog(false)}
        >
          <div
            className="w-full max-w-lg rounded-lg border border-border bg-card p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-medium">Cancelar NFSe</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Esta ação enviará um evento de cancelamento à SEFIN. Após aceito, não tem volta.
            </p>
            <form onSubmit={submitCancel} className="mt-4 space-y-3">
              <label className="block space-y-1">
                <span className="text-xs font-medium">
                  Motivo do cancelamento (15-255 caracteres)
                </span>
                <textarea
                  required
                  minLength={15}
                  maxLength={255}
                  rows={4}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex.: Servico nao executado por pedido do cliente."
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <span className="text-[11px] text-muted-foreground">
                  {motivo.trim().length} / 255
                </span>
              </label>

              {cancelErr && (
                <p role="alert" className="text-sm text-destructive">
                  {cancelErr}
                </p>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCancelDialog(false)}
                  disabled={cancel.isPending}
                  className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  disabled={cancel.isPending || motivo.trim().length < 15}
                  className="flex items-center gap-1 rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                >
                  <Ban className="h-4 w-4" />
                  {cancel.isPending ? 'Enviando…' : 'Confirmar cancelamento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Parser leve do XML retornado pela SEFIN apos emissao bem-sucedida.
 * Extrai os campos mais relevantes pra exibicao estilo DANFSe.
 */
interface ParsedNfse {
  nNFSe?: string;
  Id?: string;
  dhEmi?: string;
  dhProc?: string;
  tpAmb?: string;
  verAplic?: string;
  xLocEmi?: string;
  xLocPrestacao?: string;
  xLocIncid?: string;
  cLocIncid?: string; // codigo IBGE do municipio de incidencia (usa pra brasao)
  xTribNac?: string;
  cTribNac?: string;
  // Prestador
  prestCnpj?: string;
  prestNome?: string;
  prestIM?: string;
  // Tomador
  tomaCnpj?: string;
  tomaNome?: string;
  tomaLogradouro?: string;
  tomaNumero?: string;
  tomaBairro?: string;
  tomaCidade?: string;
  // Servico
  xDescServ?: string;
  // Valores
  vServ?: string;
  vLiq?: string;
}

function parseNfseXml(xml: string): ParsedNfse {
  const out: ParsedNfse = {};
  if (!xml) return out;

  const pick = (tag: string): string | undefined => {
    const re = new RegExp(`<${tag}>([^<]+)<\\/${tag}>`);
    const m = re.exec(xml);
    return m?.[1]?.trim();
  };
  const pickIn = (parent: string, tag: string): string | undefined => {
    const blockRe = new RegExp(`<${parent}>([\\s\\S]*?)<\\/${parent}>`);
    const block = blockRe.exec(xml)?.[1];
    if (!block) return undefined;
    const m = new RegExp(`<${tag}>([^<]+)<\\/${tag}>`).exec(block);
    return m?.[1]?.trim();
  };

  out.Id = /Id="([^"]+)"/.exec(xml)?.[1];
  out.nNFSe = pick('nNFSe');
  out.dhEmi = pick('dhEmi');
  out.dhProc = pick('dhProc');
  out.tpAmb = pick('tpAmb');
  out.verAplic = pick('verAplic');
  out.xLocEmi = pick('xLocEmi');
  out.xLocPrestacao = pick('xLocPrestacao');
  out.xLocIncid = pick('xLocIncid');
  out.cLocIncid = pick('cLocIncid');
  out.xTribNac = pick('xTribNac');
  out.cTribNac = pick('cTribNac');
  out.xDescServ = pick('xDescServ');
  out.vServ = pick('vServ');
  out.vLiq = pick('vLiq');

  out.prestCnpj = pickIn('emit', 'CNPJ') ?? pickIn('prest', 'CNPJ');
  out.prestNome = pickIn('emit', 'xNome') ?? pickIn('prest', 'xNome');
  out.prestIM = pickIn('emit', 'IM') ?? pickIn('prest', 'IM');

  out.tomaCnpj = pickIn('toma', 'CNPJ');
  out.tomaNome = pickIn('toma', 'xNome');
  out.tomaLogradouro = pickIn('toma', 'xLgr');
  out.tomaNumero = pickIn('toma', 'nro');
  out.tomaBairro = pickIn('toma', 'xBairro');
  // cMun dentro de endNac da toma
  return out;
}

function IssuedNfseDialog({
  invoice,
  onClose,
}: {
  invoice: Invoice & { client?: { id: string; razaoSocial: string; cpfCnpj: string } };
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['invoices', invoice.id, 'detail'],
    queryFn: () =>
      apiFetch<Invoice & { xmlEnvio?: string; xmlRetorno?: string }>(
        `/invoices/${invoice.id}`,
      ),
  });

  const parsed = useMemo(() => parseNfseXml(data?.xmlRetorno ?? ''), [data?.xmlRetorno]);

  function downloadXml() {
    if (!data?.xmlRetorno) return;
    const blob = new Blob([data.xmlRetorno], { type: 'application/xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nfse-${parsed.nNFSe ?? invoice.id}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function printDoc() {
    window.print();
  }

  const isHomolog = parsed.tpAmb !== '1';
  const municipio = parsed.xLocIncid ?? parsed.xLocEmi ?? 'JACAREÍ';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div
        className="relative max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Toolbar - escondida na impressao */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border bg-card/95 backdrop-blur px-4 py-2 print:hidden">
          <h2 className="flex items-center gap-2 text-sm font-medium text-emerald-700">
            <CheckCircle2 className="h-4 w-4" />
            DANFSe — Nota Nº {parsed.nNFSe ?? '—'}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={downloadXml}
              className="flex items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-accent"
            >
              <Download className="h-4 w-4" /> XML
            </button>
            <button
              type="button"
              onClick={printDoc}
              className="flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Printer className="h-4 w-4" /> Imprimir / Salvar PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-1.5 text-sm hover:bg-accent"
            >
              Fechar
            </button>
          </div>
        </div>

        {isLoading && <p className="p-6 text-sm text-muted-foreground">Carregando…</p>}

        {/* DANFSe na tela (dentro do modal) */}
        {data && (
          <DanfseDocument
            data={data}
            parsed={parsed}
            isHomolog={isHomolog}
            municipio={municipio}
          />
        )}
      </div>

      {/* Copia da DANFSe via PORTAL no body, invisivel na tela, visivel so no print.
          Isso garante que apenas ESSE node apareca quando o usuario imprime,
          sem interferencia de qualquer overflow do app. */}
      {data &&
        createPortal(
          <div className="danfse-print-portal">
            <DanfseDocument
              data={data}
              parsed={parsed}
              isHomolog={isHomolog}
              municipio={municipio}
            />
          </div>,
          document.body,
        )}

      {/* CSS print: esconde #root inteiro, mostra so o portal com a DANFSe */}
      <style>{`
        /* Portal invisivel na tela */
        .danfse-print-portal { display: none; }

        @media print {
          @page { size: A4; margin: 8mm; }
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
          }
          /* Esconde a aplicacao inteira */
          #root { display: none !important; }
          /* Mostra somente a copia da DANFSe via portal */
          .danfse-print-portal { display: block !important; }
          .danfse-print-portal .danfse {
            box-shadow: none !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Conteudo da DANFSe — usado tanto na visualizacao na tela
 * quanto na copia via portal pra impressao.
 */
function DanfseDocument({
  data,
  parsed,
  isHomolog,
  municipio,
}: {
  data: Invoice;
  parsed: ParsedNfse;
  isHomolog: boolean;
  municipio: string;
}) {
  return (
    <div
      className="danfse mx-auto bg-white p-6 text-black"
      style={{ maxWidth: '210mm', fontFamily: '"Helvetica Neue", Arial, sans-serif' }}
    >
      {/* Faixa HOMOLOGACAO */}
      {isHomolog && (
        <div className="mb-3 border-2 border-dashed border-amber-600 bg-amber-50 px-3 py-1 text-center text-xs font-bold uppercase tracking-wider text-amber-800">
          Ambiente de Homologação · NFSe sem valor fiscal
        </div>
      )}

      {/* Cabecalho oficial DANFSe (3 colunas) */}
      <div className="grid grid-cols-12 border-2 border-black">
        {/* coluna 1: brasao da prefeitura */}
        <div className="col-span-2 flex flex-col items-center justify-center gap-1 border-r-2 border-black p-2">
          {parsed.cLocIncid && (
            <img
              src={`/api/v1/municipalities/${parsed.cLocIncid}/coat-of-arms`}
              alt={`Brasão de ${municipio}`}
              className="h-20 w-auto object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <div className="text-center text-[8px] font-semibold uppercase leading-tight">
            Prefeitura Municipal
          </div>
        </div>

        {/* coluna 2: titulo */}
        <div className="col-span-7 flex flex-col items-center justify-center gap-0.5 border-r-2 border-black p-3 text-center">
          <div className="text-[10px] font-semibold uppercase tracking-wider">
            Prefeitura Municipal de {municipio}
          </div>
          <div
            className="text-base font-bold uppercase leading-tight"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            Nota Fiscal de Serviços Eletrônica
          </div>
          <div className="text-sm font-semibold">— NFS-e —</div>
          <div className="mt-1 text-[9px] text-gray-600">
            Sistema Nacional NFS-e · SEFIN · v{parsed.verAplic ?? '1.6.0'}
          </div>
          <div className="mt-2 flex items-center gap-2 border-t border-gray-300 pt-2">
            <img src={logoSimple} alt="Logo emitente" className="h-6 w-auto object-contain" />
            <span className="text-[9px] uppercase text-gray-600">
              Emitida por {parsed.prestNome ?? '—'}
            </span>
          </div>
        </div>

        {/* coluna 3: numero + data + competencia */}
        <div className="col-span-3 flex flex-col p-2 text-[10px]">
          <div className="border-b border-black pb-1">
            <div className="text-[9px] uppercase text-gray-600">Número da NFS-e</div>
            <div className="font-mono text-xl font-bold leading-tight">
              {(parsed.nNFSe ?? '0').padStart(8, '0')}
            </div>
          </div>
          <div className="mt-1 border-b border-black pb-1">
            <div className="text-[9px] uppercase text-gray-600">Data e hora emissão</div>
            <div className="font-mono text-[11px] font-semibold">{fmtDhPt(parsed.dhEmi)}</div>
          </div>
          <div className="mt-1">
            <div className="text-[9px] uppercase text-gray-600">Competência</div>
            <div className="font-mono text-[11px] font-semibold">
              {data.competencia.slice(0, 7).split('-').reverse().join('/')}
            </div>
          </div>
        </div>
      </div>

      <SectionPref title="Prestador de Serviços">
        <div className="grid grid-cols-12 gap-y-1 text-[11px]">
          <FieldPref label="Razão social" cols={12} value={parsed.prestNome ?? '—'} />
          <FieldPref label="CNPJ" cols={5} value={formatCpfCnpj(parsed.prestCnpj)} mono />
          <FieldPref label="Inscrição municipal" cols={4} value={parsed.prestIM ?? '—'} mono />
          <FieldPref label="Município" cols={3} value={parsed.xLocEmi ?? '—'} />
        </div>
      </SectionPref>

      <SectionPref title="Tomador de Serviços">
        <div className="grid grid-cols-12 gap-y-1 text-[11px]">
          <FieldPref label="Razão social / Nome" cols={9} value={parsed.tomaNome ?? '—'} />
          <FieldPref label="CNPJ / CPF" cols={3} value={formatCpfCnpj(parsed.tomaCnpj)} mono />
          <FieldPref
            label="Endereço"
            cols={12}
            value={
              [
                parsed.tomaLogradouro,
                parsed.tomaNumero && `nº ${parsed.tomaNumero}`,
                parsed.tomaBairro,
              ]
                .filter(Boolean)
                .join(' · ') || '—'
            }
          />
        </div>
      </SectionPref>

      <SectionPref title="Discriminação dos Serviços">
        <div className="min-h-[60px] whitespace-pre-wrap text-[11px] leading-relaxed">
          {parsed.xDescServ ?? '—'}
        </div>
      </SectionPref>

      <div className="border-x-2 border-b-2 border-black">
        <div className="bg-gray-100 px-2 py-1 text-[9px] font-bold uppercase tracking-wider">
          Valores do serviço
        </div>
        <div className="grid grid-cols-4 divide-x divide-black text-center text-[11px]">
          <ValuePref
            label="Valor do serviço (R$)"
            value={parsed.vServ ? formatBRL(Number(parsed.vServ)) : '—'}
          />
          <ValuePref
            label="Alíquota ISS (%)"
            value={`${Number(data.aliquotaIss).toFixed(2)}%`}
          />
          <ValuePref
            label="Valor do ISS (R$)"
            value={data.valorIss ? formatBRL(Number(data.valorIss)) : '—'}
          />
          <ValuePref
            label="Valor líquido (R$)"
            value={parsed.vLiq ? formatBRL(Number(parsed.vLiq)) : '—'}
            strong
          />
        </div>
      </div>

      <SectionPref title="Outras Informações">
        <div className="grid grid-cols-12 gap-y-1 text-[11px]">
          <FieldPref
            label="Código de tributação nacional (cTribNac)"
            cols={3}
            value={parsed.cTribNac ?? '—'}
            mono
          />
          <FieldPref
            label="Descrição da atividade"
            cols={9}
            value={parsed.xTribNac ?? '—'}
          />
          <FieldPref
            label="Local da prestação"
            cols={4}
            value={parsed.xLocPrestacao ?? '—'}
          />
          <FieldPref
            label="Município de incidência do ISSQN"
            cols={4}
            value={parsed.xLocIncid ?? '—'}
          />
          <FieldPref
            label="Data do processamento"
            cols={4}
            value={fmtDhPt(parsed.dhProc)}
          />
        </div>
      </SectionPref>

      <div className="mt-3 border-2 border-black bg-gray-50 p-2 text-center text-[9px]">
        <div className="font-semibold uppercase tracking-wider">
          Documento gerado eletronicamente pelo Sistema Nacional NFS-e
        </div>
        {parsed.Id && (
          <div className="mt-1 break-all font-mono text-[8px]">
            Chave de acesso: {parsed.Id}
          </div>
        )}
        <div className="mt-1 text-gray-600">
          Verifique a autenticidade no Portal Nacional da NFS-e (gov.br/nfse)
        </div>
      </div>
    </div>
  );
}

function SectionPref({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-x-2 border-b-2 border-black">
      <div className="bg-gray-100 px-2 py-1 text-[9px] font-bold uppercase tracking-wider">
        {title}
      </div>
      <div className="px-2 py-1.5">{children}</div>
    </div>
  );
}

// Lookup explicito pra Tailwind JIT incluir essas classes no bundle.
const COL_SPAN: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12',
};

function FieldPref({
  label,
  value,
  cols,
  mono,
}: {
  label: string;
  value: string | number;
  cols: number;
  mono?: boolean;
}) {
  return (
    <div className={`${COL_SPAN[cols] ?? 'col-span-12'} pr-2`}>
      <div className="text-[8px] uppercase text-gray-600">{label}</div>
      <div className={mono ? 'font-mono text-[10px]' : 'text-[11px]'}>{value}</div>
    </div>
  );
}

function ValuePref({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="px-2 py-1.5">
      <div className="text-[8px] uppercase text-gray-600">{label}</div>
      <div className={strong ? 'font-mono text-sm font-bold' : 'font-mono text-[12px]'}>
        {value}
      </div>
    </div>
  );
}

function fmtDhPt(s?: string): string {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return s;
  }
}

function formatCpfCnpj(s?: string): string {
  const d = (s ?? '').replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return d || '—';
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | number;
  mono?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`text-right ${mono ? 'font-mono text-xs' : 'font-medium'}`}>{value}</dd>
    </div>
  );
}

// Re-exporta ApiError pra evitar unused warning no import dinâmico de erros
export { ApiError as _InvoiceApiError };

function _formatCreatedAt(d: string): string {
  return fmtDate(d);
}
void _formatCreatedAt;
